import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hasPermission, type SessionUser } from '@voice/shared';
import { Button, EmptyState, Notice, PageHeader, Select, Tag } from '@voice/ui';
import { useState } from 'react';
import { formatDateTime, formatDuration } from '../components/common';
import { api } from '../lib/api';
import styles from './Pages.module.css';

interface Mine {
  extensions: { id: string; number: string; displayName: string }[];
}
interface Voicemail {
  id: string;
  callerNumber: string;
  callerName: string | null;
  durationMs: number;
  transcript: string | null;
  summary: string | null;
  read: boolean;
  createdAt: string;
}
interface Message {
  id: string;
  status: 'open' | 'done';
  createdAt: string;
  hasRecording: boolean;
  [key: string]: unknown;
}

export function Inbox({ user }: { user: SessionUser }) {
  const mine = useQuery({ queryKey: ['me-extension'], queryFn: () => api<Mine>('/me/extension') });
  const [extId, setExtId] = useState('');
  const exts = mine.data?.extensions ?? [];
  const active = extId || exts[0]?.id || '';
  const canMessages = hasPermission(user.permissions, 'messages.manage');
  return (
    <div className={styles.page}>
      <PageHeader title="Nachrichten" />
      {exts.length > 1 && (
        <Select label="Anrufbeantworter von" value={active} onChange={(e) => setExtId(e.target.value)} style={{ maxWidth: 320, marginBottom: 20 }}>
          {exts.map((e) => (
            <option key={e.id} value={e.id}>
              {e.number} – {e.displayName}
            </option>
          ))}
        </Select>
      )}
      {active ? <Mailbox url={`/voicemail?extensionId=${active}`} keyId={active} title="Mein Anrufbeantworter" /> : <EmptyState>Dir ist keine Nebenstelle zugeordnet, daher gibt es keinen Anrufbeantworter.</EmptyState>}
      <QueueMailboxes />
      {canMessages && <SecretaryMessages />}
    </div>
  );
}

function QueueMailboxes() {
  const query = useQuery({ queryKey: ['voicemail', 'queues'], queryFn: () => api<{ queues: { id: string; name: string; unread: number }[] }>('/voicemail/queues') });
  return (
    <>
      {query.data?.queues.map((q) => (
        <div key={q.id} style={{ marginTop: 32 }}>
          <Mailbox url={`/voicemail/queue/${q.id}`} keyId={q.id} title={`Anrufbeantworter ${q.name}`} />
        </div>
      ))}
    </>
  );
}

function Mailbox({ url, keyId, title }: { url: string; keyId: string; title: string }) {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['voicemail', keyId], queryFn: () => api<{ messages: Voicemail[] }>(url) });
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['voicemail'] }), qc.invalidateQueries({ queryKey: ['voicemail-count'] })]);
  const mark = useMutation({ mutationFn: (v: { id: string; read: boolean }) => api(`/voicemail/${v.id}`, { method: 'PATCH', body: { read: v.read } }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: string) => api(`/voicemail/${id}`, { method: 'DELETE' }), onSuccess: refresh });
  return (
    <section>
      <h2 className={styles.heading}>{title}</h2>
      {query.isError && <Notice tone="error">Nachrichten konnten nicht geladen werden.</Notice>}
      {query.data?.messages.length === 0 && <EmptyState>Keine Nachrichten.</EmptyState>}
      <div className={styles.deviceList}>
        {query.data?.messages.map((m) => (
          <div key={m.id} className={styles.device} style={{ alignItems: 'flex-start' }}>
            <div className={styles.deviceInfo} style={{ flex: 1 }}>
              <span className={styles.deviceName}>
                {!m.read && <Tag tone="warn">neu</Tag>} {m.callerName ?? m.callerNumber}
              </span>
              <span className={styles.muted}>
                {m.callerNumber} · {formatDateTime(m.createdAt)} · {formatDuration(m.durationMs)}
              </span>
              {m.summary && <p style={{ margin: '6px 0', fontWeight: 600 }}>{m.summary}</p>}
              {m.transcript && <p style={{ margin: '6px 0' }}>{m.transcript}</p>}
              <audio controls preload="none" src={`/api/voicemail/${m.id}/audio`} onPlay={() => !m.read && mark.mutate({ id: m.id, read: true })} style={{ maxWidth: '100%' }} />
            </div>
            <div className={styles.inline}>
              <Button variant="secondary" onClick={() => mark.mutate({ id: m.id, read: !m.read })}>
                {m.read ? 'Als neu markieren' : 'Gelesen'}
              </Button>
              <Button variant="secondary" onClick={() => confirm('Nachricht löschen?') && remove.mutate(m.id)}>
                Löschen
              </Button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SecretaryMessages() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<'open' | 'done'>('open');
  const query = useQuery({ queryKey: ['messages', status], queryFn: () => api<{ messages: Message[] }>(`/messages?status=${status}`) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['messages'] });
  const setDone = useMutation({ mutationFn: (v: { id: string; status: 'open' | 'done' }) => api(`/messages/${v.id}`, { method: 'PATCH', body: { status: v.status } }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: (id: string) => api(`/messages/${id}`, { method: 'DELETE' }), onSuccess: refresh });
  const skip = new Set(['id', 'status', 'createdAt', 'hasRecording', 'updatedAt', 'recordingFile', 'assignedExtensionId']);
  return (
    <section className={styles.section}>
      <div className={styles.inline} style={{ justifyContent: 'space-between' }}>
        <h2 className={styles.heading}>Nachrichten aus dem Sekretariat</h2>
        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value as 'open' | 'done')}>
          <option value="open">Offen</option>
          <option value="done">Erledigt</option>
        </Select>
      </div>
      {query.data?.messages.length === 0 && <EmptyState>Keine Nachrichten.</EmptyState>}
      <div className={styles.deviceList}>
        {query.data?.messages.map((m) => (
          <div key={m.id} className={styles.device} style={{ alignItems: 'flex-start' }}>
            <div className={styles.deviceInfo} style={{ flex: 1 }}>
              <span className={styles.muted}>{formatDateTime(m.createdAt)}</span>
              {Object.entries(m)
                .filter(([k, v]) => !skip.has(k) && v !== null && v !== '' && typeof v !== 'object')
                .map(([k, v]) => (
                  <div key={k}>
                    <span className={styles.muted}>{k}: </span>
                    {String(v)}
                  </div>
                ))}
              {m.hasRecording && <audio controls preload="none" src={`/api/messages/${m.id}/audio`} style={{ maxWidth: '100%' }} />}
            </div>
            <div className={styles.inline}>
              <Button variant="secondary" onClick={() => setDone.mutate({ id: m.id, status: status === 'open' ? 'done' : 'open' })}>
                {status === 'open' ? 'Erledigt' : 'Wieder öffnen'}
              </Button>
              <Button variant="secondary" onClick={() => confirm('Nachricht löschen?') && remove.mutate(m.id)}>
                Löschen
              </Button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
